import { ButtonBase, type ButtonBaseProps } from "@mui/material";
import type { ReactNode } from "react";

type TextButtonProps = ButtonBaseProps & {
  /** Optional trailing icon, e.g. an arrow. Rendered after the label. */
  endIcon?: ReactNode;
};

/**
 * House text button: all-caps Lato, 11px/700, letter-spaced.
 * Colour is the text-safe --color-dust-ink (AA on white/clear-dust;
 * do NOT place on --color-light-dust — see index.css note).
 * Renders a real <button> for accessibility (keyboard + focus ring).
 *
 * Arrow convention: pass `endIcon={<ArrowForward />}` ONLY when the action means
 * "go / see more / continue" (e.g. "View all"). Omit it for in-place actions
 * (Edit, Remove, Cancel, Save) — a trailing arrow implies navigation that
 * doesn't happen.
 */
const TextButton = ({ children, endIcon, sx, ...rest }: TextButtonProps) => {
  return (
    <ButtonBase
      disableRipple
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        px: "4px",
        py: "2px",
        borderRadius: "4px",
        fontFamily: "var(--font-lato)",
        fontWeight: 700,
        fontSize: "11px",
        lineHeight: 1,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "var(--color-dust-ink)",
        whiteSpace: "nowrap",
        transition: "color 120ms ease",
        "&:hover": { color: "var(--color-rodeo-black)" },
        "&:focus-visible": {
          outline: "2px solid var(--color-dust-ink)",
          outlineOffset: "2px",
        },
        ...sx,
      }}
      {...rest}
    >
      {children}
      {endIcon}
    </ButtonBase>
  );
};

export default TextButton;
